import * as React from 'react';
import { UIEvent, getPageX, getPageY } from '../../uievent';

interface SliderProps {
  readonly vertical: boolean;
  readonly enabled: boolean;
  readonly value: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly step?: number;
  readonly sliderLength: number;
  readonly sliderWidth: number;
  readonly barStyle?: React.CSSProperties;
  readonly gripSize: number;
  readonly gripStyle?: React.CSSProperties;
  readonly onChange: (value: number) => void;
  readonly renderIncrementButton?: () => JSX.Element;
  readonly renderDecrementButton?: () => JSX.Element;
  readonly renderTrackMarker?: () => JSX.Element;
}

interface SliderState {
  readonly dragging: boolean;
  readonly mousePrevX: number | null;
  readonly mousePrevY: number | null;
  readonly value: number;
}

export class Slider extends React.Component<SliderProps, SliderState> {
  private grip: HTMLElement | null = null;

  public static defaultProps = {
    sliderLength: 110,
    sliderWidth: 10,
    gripSize: 20,
  };

  public constructor(props: SliderProps) {
    super(props);

    const { minValue, maxValue, value } = props;
    if (maxValue - minValue <= 0) {
      throw new Error('maxValue - minValue <= 0');
    }

    this.state = {
      value,
      dragging: false,
      mousePrevX: null,
      mousePrevY: null,
    };
  }

  private readonly handlePointerDown = (ev: UIEvent<HTMLElement>): void => {
    if (!this.props.enabled) {
      return;
    }

    this.setState({
      dragging: true,
      mousePrevX: getPageX(ev),
      mousePrevY: getPageY(ev),
    });
    this.startMouseCapture();
  }

  // FIXME : Resizableからコピペが多い
  private readonly handlePointerMove = (ev: MouseEvent | TouchEvent) => {
    const {
      vertical, minValue, maxValue, step, sliderLength, onChange,
    } = this.props;

    if (!this.state.dragging) {
      return;
    }

    // iOS Safariにおいて、getBoundingClientRect()の座標がバウンスしたかのような座標になるのを防止
    ev.preventDefault();

    const pageX = ev instanceof MouseEvent ? ev.pageX : ev.changedTouches[0].pageX;
    const pageY = ev instanceof MouseEvent ? ev.pageY : ev.changedTouches[0].pageY;

    let dx = pageX - this.state.mousePrevX!;
    let dy = pageY - this.state.mousePrevY!;

    // 最大/最小サイズ制約などで、グリップとマウスカーソルの位置がずれていく場合がある。
    // 自然な挙動にするため、逆方向に戻したときは、元のグリップ位置に到達するまでリサイズしない。
    const { right: gripX, bottom: gripY } = this.grip!.getBoundingClientRect();
    if (dx > 0 && pageX < gripX || dx < 0 && pageX > gripX) {
      dx = 0;
    }
    if (dy > 0 && pageY < gripY || dy < 0 && pageY > gripY) {
      dy = 0;
    }

    const dist = (vertical ? -dy : dx) * (maxValue / sliderLength);
    const newValue = Math.min(Math.max(minValue, this.state.value + dist), maxValue);

    this.setState({
      value: newValue,
      mousePrevX: pageX,
      mousePrevY: pageY,
    });

    if (step !== undefined) {
      const roundedNewValue =  Math.round(newValue / step) * step;
      if (this.state.value !== roundedNewValue) {
        onChange(roundedNewValue);
        return;
      }
    }

    onChange(newValue);
  }

  private readonly handlePointerUp = (_ev: MouseEvent | TouchEvent) => {
    this.stopMouseCapture();

    this.setState({ dragging: false });
  }

  private stopMouseCapture(): void {
    if (this.grip !== null) {
      const { ownerDocument } = this.grip;
      if (ownerDocument === null) {
        return;
      }

      ownerDocument.removeEventListener('mousemove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('mouseup', this.handlePointerUp, true);
      ownerDocument.removeEventListener('touchmove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('touchend', this.handlePointerUp, true);
    }
  }

  private startMouseCapture(): void {
    if (this.grip === null) {
      throw new Error('Illegal state.');
    }

    const { ownerDocument } = this.grip;
    if (ownerDocument === null) {
      return;
    }

    ownerDocument.addEventListener('mousemove', this.handlePointerMove, true);
    ownerDocument.addEventListener('mouseup', this.handlePointerUp, true);
    ownerDocument.addEventListener('touchmove', this.handlePointerMove, true);
    ownerDocument.addEventListener('touchend', this.handlePointerUp, true);
  }

  public render() {
    const {
      vertical, enabled,
      value, minValue, maxValue, sliderLength, sliderWidth, barStyle, gripSize, gripStyle,
      renderIncrementButton, renderDecrementButton, renderTrackMarker,
    } = this.props;

    const barBaseStyle: React.CSSProperties = {
      background: '#b0d0ef',
      position: 'relative',
    };

    const gripBaseStyle: React.CSSProperties = {
      width: `${gripSize}px`,
      height: `${gripSize}px`,
      boxSizing: 'border-box',
      position: 'absolute',
      cursor: 'pointer',
      overflow: 'visible',
    };

    const offset = (value - minValue) / (maxValue - minValue) * (sliderLength - gripSize);

    if (vertical) {
      barBaseStyle.width = `${sliderWidth}px`;
      barBaseStyle.height = `${sliderLength}px`;
      gripBaseStyle.bottom = `${offset}px`;
      gripBaseStyle.left = `${(sliderWidth - gripSize) / 2}px`;
    } else {
      barBaseStyle.width = `${sliderLength}px`;
      barBaseStyle.height = `${sliderWidth}px`;
      gripBaseStyle.top = `${(sliderWidth - gripSize) / 2}px`;
      gripBaseStyle.left = `${offset}px`;
    }

    if (!enabled) {
      barBaseStyle.filter = 'brightness(0.5)';
    }

    const trackMarker = renderTrackMarker != null ? renderTrackMarker() : null;
    const gripPos = `${gripSize / 2}px`;

    const slider = (
      <div key="zoom-slider" style={{ ...barBaseStyle, ...barStyle }}>
        <div
          style={{ ...gripBaseStyle, ...gripStyle }}
          ref={el => this.grip = el}
          onMouseDown={this.handlePointerDown}
          onTouchStart={this.handlePointerDown}>
          <div style={{ position: 'absolute', top: gripPos, left: gripPos }}>
            {trackMarker}
          </div>
        </div>
      </div>
    );

    const incrementButton = renderIncrementButton != null ? renderIncrementButton() : null;
    const decrementButton = renderDecrementButton != null ? renderDecrementButton() : null;

    if (vertical) {
      return <>{incrementButton}{slider}{decrementButton}</>;
    }
    return <>{decrementButton}{slider}{incrementButton}</>;
  }

  public static getDerivedStateFromProps(
    props: Readonly<SliderProps>,
    state: Readonly<SliderState>,
  ) {
    if (!state.dragging) {
      return { ...state, value: props.value };
    }

    return null;
  }
}
